// 고객 단계는 사건이 정한다 — 규칙·이력 한 줄의 모양은 한 벌(인메모리·Supabase·화면 버튼이 같이 쓴다).

import type { ContractStatus } from "../contract/types";
import type { CodeEntry } from "../org/codes";
import { firstContactDue } from "./first-contact";
import { STAGE_CHANGED_ACTION, stageChangedSummary } from "./stage-activity";
import type { Customer, CustomerStage } from "./types";
import type { Quote } from "../quote/types";

export const STAGE_CAUSES = [
  "QUOTE_STARTED",
  "QUOTE_SENT",
  "QUOTE_WON",
  "QUOTE_LOST",
  "CONTRACT_SIGNED",
  "MANUAL_CONTACT",
  "MANUAL_LOST",
  "REVERT",
] as const;
export type StageCause = (typeof STAGE_CAUSES)[number];

export const STAGE_CAUSE_LABEL: Record<StageCause, string> = {
  QUOTE_STARTED: "견적 시작",
  QUOTE_SENT: "견적 발송",
  QUOTE_WON: "견적 수주",
  QUOTE_LOST: "견적 이탈",
  CONTRACT_SIGNED: "계약 서명",
  MANUAL_CONTACT: "상담함",
  MANUAL_LOST: "이탈 처리",
  REVERT: "되돌리기",
};

// 사람이 누르지 않고 견적·계약 사건이 옮긴 원인 — 이력 행위자 옆에 「자동」 을 붙인다.
export const SYSTEM_STAGE_CAUSES: readonly StageCause[] = ["QUOTE_STARTED", "QUOTE_SENT", "QUOTE_WON", "QUOTE_LOST", "CONTRACT_SIGNED"];

export const stageEventActorText = (
  event: Pick<CustomerStageEvent, "cause" | "actor_id">,
  name: string | null | undefined
): string => {
  if (!event.actor_id) return "자동";
  const who = name ?? event.actor_id;
  return SYSTEM_STAGE_CAUSES.includes(event.cause) ? `${who}(자동)` : who;
};

// 이력 한 줄의 사유 — 이탈로 간 이력은 이탈 코드 표기를 따로 보이고, 견적 이탈 사유가 그 표기와 같으면 한 줄만 남긴다.
export const stageEventReasons = (
  event: Pick<CustomerStageEvent, "to_stage" | "lost_code" | "reason">,
  labelOf: (code: string) => string
): { lost: string | null; reason: string | null } => {
  const lost = event.to_stage === "LOST" && event.lost_code ? labelOf(event.lost_code) : null;
  const reason = event.reason && event.reason !== lost && event.reason !== event.lost_code ? event.reason : null;
  return { lost, reason };
};

// 사유 길이 상한 — 0065 의 check 와 같은 값.
export const STAGE_REASON_MAX = 300;

export const STAGE_REF_TYPES = ["quote", "contract"] as const;
export type StageRefType = (typeof STAGE_REF_TYPES)[number];

export interface CustomerStageEvent {
  id: string;
  org_id: string;
  customer_id: string;
  from_stage: CustomerStage;
  to_stage: CustomerStage;
  cause: StageCause;
  reason: string | null;
  ref_type: StageRefType | null;
  ref_id: string | null;
  // 이탈로 옮긴 이력만 — 그때의 이탈 코드.
  lost_code: string | null;
  // null 이면 시스템(견적·계약 사건)이 바꾼 것.
  actor_id: string | null;
  created_at: string;
}

export type StageEventInput =
  | { cause: "QUOTE_STARTED"; quote_id: string }
  // via — 담당자 발송(send)은 이탈 고객도 되살리고, 고객 첫 열람(view)은 신규·상담만 옮긴다.
  | { cause: "QUOTE_SENT"; quote_id: string; via: "send" | "view" }
  | { cause: "QUOTE_WON"; quote_id: string }
  | { cause: "QUOTE_LOST"; quote_id: string; reason: string | null; lost_code: string | null }
  | { cause: "CONTRACT_SIGNED"; contract_id: string }
  | { cause: "MANUAL_CONTACT" }
  | { cause: "MANUAL_LOST"; lost_code: string }
  | { cause: "REVERT"; reason: string };

export interface StageFacts {
  customer: Pick<Customer, "id" | "stage" | "lost_reason">;
  last_event: Pick<CustomerStageEvent, "from_stage" | "to_stage" | "cause"> | null;
  // 마지막 이력 바로 앞 이력의 이탈 코드 — 되돌려 이탈로 갈 때 그 사유를 되살린다(없으면 null).
  previous_lost_code: string | null;
  // 체결된 계약(SIGNED·COMPLETED)이 하나라도 있는가 — 있으면 되돌리기를 막는다.
  has_signed_contract: boolean;
  quotes: Pick<Quote, "id" | "quote_no" | "status">[];
}

export type StagePlan =
  | { kind: "change"; to: CustomerStage; lost_reason: string | null }
  | { kind: "same" }
  | { kind: "blocked"; message: string };

const SAME: StagePlan = { kind: "same" };
const blocked = (message: string): StagePlan => ({ kind: "blocked", message });

const change = (facts: StageFacts, to: CustomerStage, lostCode?: string | null): StagePlan =>
  facts.customer.stage === to
    ? SAME
    : { kind: "change", to, lost_reason: to === "LOST" ? (lostCode ?? facts.customer.lost_reason) : null };

// 계열(quote_no) 안에 수주·이탈이 하나라도 있으면 끝난 계열이다.
const decidedSeries = (quotes: StageFacts["quotes"]): Set<string> =>
  new Set(quotes.filter((q) => q.status === "WON" || q.status === "LOST").map((q) => q.quote_no));

const seriesOf = (facts: StageFacts, quoteId: string): string | null =>
  facts.quotes.find((q) => q.id === quoteId)?.quote_no ?? null;

// 이 계열 말고 아직 결론 안 난(초안 포함) 견적 계열이 남았는가.
export const hasOtherLiveQuote = (facts: StageFacts, quoteId: string): boolean => {
  const own = seriesOf(facts, quoteId);
  const decided = decidedSeries(facts.quotes);
  return facts.quotes.some((q) => q.quote_no !== own && !decided.has(q.quote_no));
};

export const REVERT_SIGNED_MESSAGE = "계약 체결로 바뀐 단계는 되돌릴 수 없습니다";

// 체결이 살아 있는 계약 상태 — 파기·해지는 빠진다.
export const SIGNED_CONTRACT_STATUSES: readonly ContractStatus[] = ["SIGNED", "COMPLETED"];

// 기준은 지금 체결된 계약이 있는가 하나 — 해지되면 어느 경로로 서명했든 다시 되돌릴 수 있다.
const revertLockedBySigning = (facts: StageFacts): boolean => facts.has_signed_contract;

// 되돌릴 수 있는가 — 마지막 이력이 지금 단계를 만든 것이고, 되돌리기가 아니며 체결된 계약이 없어야 한다.
export const revertTarget = (facts: StageFacts): CustomerStage | null => {
  const last = facts.last_event;
  if (!last || last.to_stage !== facts.customer.stage) return null;
  if (last.cause === "REVERT" || revertLockedBySigning(facts)) return null;
  return last.from_stage;
};

export const planStageEvent = (facts: StageFacts, event: StageEventInput): StagePlan => {
  const stage = facts.customer.stage;
  switch (event.cause) {
    case "QUOTE_STARTED":
      return stage === "NEW" ? change(facts, "CONTACTED") : SAME;
    case "QUOTE_SENT": {
      // 결론 난 계열의 첫 열람(옛 링크)은 단계를 되살리지 않는다.
      const series = seriesOf(facts, event.quote_id);
      if (series !== null && decidedSeries(facts.quotes).has(series)) return SAME;
      if (stage === "WON" || (stage === "LOST" && event.via === "view")) return SAME;
      return change(facts, "QUOTED");
    }
    case "QUOTE_WON":
    case "CONTRACT_SIGNED":
      return change(facts, "WON");
    case "QUOTE_LOST":
      if (stage === "WON" || hasOtherLiveQuote(facts, event.quote_id)) return SAME;
      return change(facts, "LOST", event.lost_code);
    case "MANUAL_CONTACT":
      return stage === "NEW" ? change(facts, "CONTACTED") : blocked("신규 고객만 「상담함」으로 바꿀 수 있습니다");
    case "MANUAL_LOST":
      if (stage === "WON") return blocked("수주한 고객은 이탈 처리할 수 없습니다");
      if (stage === "LOST") return blocked("이미 이탈 처리한 고객입니다");
      return change(facts, "LOST", event.lost_code);
    case "REVERT": {
      if (revertLockedBySigning(facts)) return blocked(REVERT_SIGNED_MESSAGE);
      const target = revertTarget(facts);
      if (target === null) return blocked("되돌릴 단계 변경이 없습니다");
      return target === "LOST" ? { kind: "change", to: "LOST", lost_reason: facts.previous_lost_code } : change(facts, target);
    }
  }
};

// 화면 버튼 — 누르면 막히지 않는 것만 보인다.
export const stageButtons = (facts: StageFacts): { contact: boolean; lost: boolean; revert: boolean } => ({
  contact: facts.customer.stage === "NEW",
  lost: facts.customer.stage !== "WON" && facts.customer.stage !== "LOST",
  revert: revertTarget(facts) !== null,
});

// 견적 이탈 사유(자유 입력)가 조직 이탈 코드의 code·표기와 같으면 그 코드 — 아니면 null(기존 사유 유지).
export const lostCodeOf = (entries: CodeEntry[], text: string | null | undefined): string | null => {
  const value = text?.trim();
  if (!value) return null;
  const hit = entries.find((row) => row.active && (row.code === value || row.label.trim() === value));
  return hit?.code ?? null;
};

export interface StageEventWrite {
  id: string;
  customer_id: string;
  expected_from: CustomerStage;
  to_stage: CustomerStage;
  cause: StageCause;
  reason: string | null;
  ref_type: StageRefType | null;
  ref_id: string | null;
  actor_id: string | null;
  lost_reason: string | null;
  // 이 이력이 이탈로 옮겼을 때의 이탈 코드 — 나중에 되돌려 이탈로 갈 때 쓴다.
  lost_code: string | null;
  // 비어 있을 때만 채운다(RPC 는 coalesce) — 첫 응대 단계가 아니면 null.
  first_contacted_at: string | null;
  activity: {
    actor_id: string | null;
    action: string;
    target_type: "customer";
    target_id: string;
    summary: string;
    meta: Record<string, string>;
  };
}

const refOf = (event: StageEventInput): { ref_type: StageRefType | null; ref_id: string | null } => {
  if ("quote_id" in event) return { ref_type: "quote", ref_id: event.quote_id };
  if ("contract_id" in event) return { ref_type: "contract", ref_id: event.contract_id };
  return { ref_type: null, ref_id: null };
};

const reasonOf = (event: StageEventInput): string | null => {
  const raw = event.cause === "QUOTE_LOST" || event.cause === "REVERT" ? event.reason : null;
  const text = raw?.trim() ?? "";
  return text ? text.slice(0, STAGE_REASON_MAX) : null;
};

export const buildStageWrite = (
  facts: StageFacts,
  event: StageEventInput,
  plan: Extract<StagePlan, { kind: "change" }>,
  meta: { id: string; actor_id: string | null; at: string }
): StageEventWrite => {
  const from = facts.customer.stage;
  return {
    id: meta.id,
    customer_id: facts.customer.id,
    expected_from: from,
    to_stage: plan.to,
    cause: event.cause,
    reason: reasonOf(event),
    ...refOf(event),
    actor_id: meta.actor_id,
    lost_reason: plan.lost_reason,
    lost_code: plan.to === "LOST" ? plan.lost_reason : null,
    first_contacted_at: firstContactDue(plan.to) ? meta.at : null,
    activity: {
      actor_id: meta.actor_id,
      action: STAGE_CHANGED_ACTION,
      target_type: "customer",
      target_id: facts.customer.id,
      summary: stageChangedSummary(from, plan.to),
      meta: { from, to: plan.to, cause: event.cause },
    },
  };
};

export type StageStep =
  | { kind: "write"; write: StageEventWrite }
  | { kind: "same" }
  | { kind: "blocked"; message: string };

// 규칙 → 쓰기 한 벌. 저장소 두 벌과 시드가 같은 순서로 부른다.
export const stageStep = (
  facts: StageFacts,
  event: StageEventInput,
  meta: { id: string; actor_id: string | null; at: string }
): StageStep => {
  const plan = planStageEvent(facts, event);
  return plan.kind === "change" ? { kind: "write", write: buildStageWrite(facts, event, plan, meta) } : plan;
};

export type StageTarget = { customer_id: string } | { quote_id: string };
